import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { get, post, put } from "../../Services/ApiRequest";
import { API_URLS } from "../../Utils/Constants/APIConstants";
import { toast } from "../../Utils/Helper/toast";

/**
 * Izba Admin → Config → Account Plans: which organizations have Account Plans.
 * Off by default everywhere; an Izba admin turns it on per organization. Turning
 * it off hides it and refuses its routes, and deletes nothing. Plan chat ("Ask about
 * this plan") has its own switch per organization, shown only where Account Plans is on.
 */

export interface AccessChange {
  enabled: boolean;
  note: string | null;
  by: string;
  at: string | null;
}

export interface OrgAccess {
  schema: string;
  name: string;
  is_active: boolean;
  /** The organization's own choice; null = it follows the default. */
  setting: boolean | null;
  /** On for this organization (before the kill switch). */
  enabled: boolean;
  /** Plan chat shows for this organization (Account Plans on and chat on). */
  chat: boolean;
  /** The organization's own chat choice; null = it follows the chat default. */
  chat_setting?: boolean | null;
  plan_versions: number;
  last_change: AccessChange | null;
  chat_last_change?: AccessChange | null;
  /** How often (days) this organization asks for a plan refresh; the server defaults it to 30. */
  refresh_days?: number;
  /** How long an upload that didn't become a plan is kept, in days (failure audit Q3). */
  unused_upload_days?: number;
}

/** One account found overdue by a refresh check. */
export interface RefreshOverdueAccount {
  account_id: string;
  account_name: string;
  published_plan_version: number;
  published_at: string | null;
  due_at: string | null;
}

/** What "Check now" found for one organization. */
export interface RefreshCheckResult {
  checked: number;
  overdue: RefreshOverdueAccount[];
  notified: number;
}

/** One processing lane: slots set here, the server's ceiling, and what it's doing now. */
export interface QueueLane {
  set: number | null;
  ceiling: number;
  effective: number;
  running: number;
  queued: number;
  oldest_wait_s: number;
  longest_wait_24h_s: number;
}

export interface QueueSlots {
  read: QueueLane;
  brief: QueueLane;
  cache_seconds: number;
  updated_at: string | null;
  updated_by: string | null;
  note: string | null;
}

export interface AccessOverview {
  default_enabled: boolean;
  chat_default_enabled?: boolean;
  kill_switch: { engaged: boolean; env: boolean; runtime: boolean };
  cache_seconds: number;
  /** Processing slots for every organization together (spec QUE-08..10). */
  queue?: QueueSlots | null;
  organizations: OrgAccess[];
}

interface AccountPlanAccessState {
  overview: AccessOverview | null;
  loading: boolean;
  loadError: string | null;
  /** The organization being switched (schema, or schema + ":chat"), while its request is in flight. */
  saving: string | null;
  /** The last refresh check per organization (schema), shown inline on its row. */
  refreshChecks: Record<string, RefreshCheckResult>;
  /** Organizations (schema) whose refresh check is in flight. */
  checking: string[];
}

const initialState: AccountPlanAccessState = {
  overview: null,
  loading: false,
  loadError: null,
  saving: null,
  refreshChecks: {},
  checking: [],
};

const errorMessage = (error: unknown, fallback: string): string => {
  const anyError = error as {
    response?: { data?: { detail?: unknown; message?: unknown } };
  };
  const detail =
    anyError?.response?.data?.detail || anyError?.response?.data?.message;
  return typeof detail === "string" && detail.trim() ? detail : fallback;
};

const isOverview = (value: unknown): value is AccessOverview =>
  !!value &&
  typeof value === "object" &&
  Array.isArray((value as AccessOverview).organizations);

export const fetchAccountPlanAccess = createAsyncThunk<
  AccessOverview,
  void,
  { rejectValue: string }
>("accountPlanAccess/fetch", async (_, { rejectWithValue }) => {
  try {
    const response = await get(API_URLS.accountPlansAccess());
    if (!isOverview(response?.content)) {
      return rejectWithValue("Invalid Account Plans access response");
    }
    return response.content;
  } catch (error: unknown) {
    return rejectWithValue(
      errorMessage(error, "Failed to load who has Account Plans.")
    );
  }
});

export const setAccountPlanAccess = createAsyncThunk<
  AccessOverview,
  { schema: string; name: string; enabled: boolean; note?: string },
  { rejectValue: string }
>(
  "accountPlanAccess/set",
  async ({ schema, name, enabled, note }, { rejectWithValue }) => {
    try {
      const response = await put(API_URLS.accountPlansAccess(schema), {
        enabled,
        note: note?.trim() || null,
      });
      if (!isOverview(response?.content)) {
        return rejectWithValue("Invalid Account Plans access response");
      }
      toast.success(
        `Account Plans turned ${enabled ? "on" : "off"} for ${name}`
      );
      return response.content;
    } catch (error: unknown) {
      const message = errorMessage(
        error,
        `Couldn't turn Account Plans ${enabled ? "on" : "off"} for ${name}.`
      );
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const setAccountPlanChat = createAsyncThunk<
  AccessOverview,
  { schema: string; name: string; enabled: boolean; note?: string },
  { rejectValue: string }
>(
  "accountPlanAccess/setChat",
  async ({ schema, name, enabled, note }, { rejectWithValue }) => {
    try {
      const response = await put(API_URLS.accountPlansChatAccess(schema), {
        enabled,
        note: note?.trim() || null,
      });
      if (!isOverview(response?.content)) {
        return rejectWithValue("Invalid Account Plans access response");
      }
      toast.success(`Plan chat turned ${enabled ? "on" : "off"} for ${name}`);
      return response.content;
    } catch (error: unknown) {
      const message = errorMessage(
        error,
        `Couldn't turn plan chat ${enabled ? "on" : "off"} for ${name}.`
      );
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

/** How many plans are read and briefed at once (all organizations; capped by the server). */
export const setQueueSlots = createAsyncThunk<
  AccessOverview,
  { read: number | null; brief: number | null; note?: string },
  { rejectValue: string }
>(
  "accountPlanAccess/setQueueSlots",
  async ({ read, brief, note }, { rejectWithValue }) => {
    try {
      const response = await put(API_URLS.accountPlansAccess("queue-slots"), {
        read_slots: read,
        brief_slots: brief,
        note: note?.trim() || null,
      });
      if (!isOverview(response?.content)) {
        return rejectWithValue("Invalid Account Plans access response");
      }
      toast.success("Processing slots saved");
      return response.content;
    } catch (error: unknown) {
      const message = errorMessage(
        error,
        "Couldn't save the processing slots."
      );
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

/** How often an organization asks for its account plans to be refreshed. */
export const setRefreshDays = createAsyncThunk<
  AccessOverview,
  { schema: string; name: string; days: number; note?: string },
  { rejectValue: string }
>(
  "accountPlanAccess/setRefreshDays",
  async ({ schema, name, days, note }, { rejectWithValue }) => {
    try {
      const response = await put(API_URLS.accountPlansRefreshDays(schema), {
        days,
        note: note?.trim() || null,
      });
      if (!isOverview(response?.content)) {
        return rejectWithValue("Invalid Account Plans access response");
      }
      toast.success(`Plan refresh set to every ${days} days for ${name}`);
      return response.content;
    } catch (error: unknown) {
      const message = errorMessage(
        error,
        `Couldn't change the plan refresh for ${name}.`
      );
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

/** How long an organization keeps uploads that didn't become a plan (failure audit Q3). */
export const setUnusedUploadDays = createAsyncThunk<
  AccessOverview,
  { schema: string; name: string; days: number; note?: string },
  { rejectValue: string }
>(
  "accountPlanAccess/setUnusedUploadDays",
  async ({ schema, name, days, note }, { rejectWithValue }) => {
    try {
      const response = await put(
        API_URLS.accountPlansUnusedUploadDays(schema),
        {
          days,
          note: note?.trim() || null,
        }
      );
      if (!isOverview(response?.content)) {
        return rejectWithValue("Invalid Account Plans access response");
      }
      toast.success(`Unused uploads kept for ${days} days for ${name}`);
      return response.content;
    } catch (error: unknown) {
      const message = errorMessage(
        error,
        `Couldn't change how long ${name} keeps unused uploads.`
      );
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

const isRefreshCheck = (value: unknown): value is RefreshCheckResult =>
  !!value &&
  typeof value === "object" &&
  typeof (value as RefreshCheckResult).checked === "number" &&
  Array.isArray((value as RefreshCheckResult).overdue);

/** Run the overdue-plan check for one organization now (bells go to the owners). */
export const runRefreshCheck = createAsyncThunk<
  RefreshCheckResult,
  { schema: string; name: string },
  { rejectValue: string }
>(
  "accountPlanAccess/runRefreshCheck",
  async ({ schema, name }, { rejectWithValue }) => {
    try {
      const response = await post(
        API_URLS.accountPlansRefreshCheck(schema),
        {}
      );
      if (!isRefreshCheck(response?.content)) {
        return rejectWithValue("Invalid refresh check response");
      }
      return response.content;
    } catch (error: unknown) {
      const message = errorMessage(
        error,
        `Couldn't check plan refreshes for ${name}.`
      );
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

const accountPlanAccessSlice = createSlice({
  name: "accountPlanAccess",
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchAccountPlanAccess.pending, state => {
        state.loading = true;
        state.loadError = null;
      })
      .addCase(fetchAccountPlanAccess.fulfilled, (state, action) => {
        state.loading = false;
        state.overview = action.payload;
      })
      .addCase(fetchAccountPlanAccess.rejected, (state, action) => {
        state.loading = false;
        state.loadError = action.payload || "Failed to load.";
      })
      .addCase(setAccountPlanAccess.pending, (state, action) => {
        state.saving = action.meta.arg.schema;
      })
      .addCase(setAccountPlanAccess.fulfilled, (state, action) => {
        state.saving = null;
        state.overview = action.payload;
      })
      .addCase(setAccountPlanAccess.rejected, state => {
        state.saving = null;
      })
      .addCase(setAccountPlanChat.pending, (state, action) => {
        state.saving = `${action.meta.arg.schema}:chat`;
      })
      .addCase(setAccountPlanChat.fulfilled, (state, action) => {
        state.saving = null;
        state.overview = action.payload;
      })
      .addCase(setAccountPlanChat.rejected, state => {
        state.saving = null;
      })
      .addCase(setQueueSlots.pending, state => {
        state.saving = "queue-slots";
      })
      .addCase(setQueueSlots.fulfilled, (state, action) => {
        state.saving = null;
        state.overview = action.payload;
      })
      .addCase(setQueueSlots.rejected, state => {
        state.saving = null;
      })
      .addCase(setRefreshDays.pending, (state, action) => {
        state.saving = `${action.meta.arg.schema}:refresh`;
      })
      .addCase(setRefreshDays.fulfilled, (state, action) => {
        state.saving = null;
        state.overview = action.payload;
      })
      .addCase(setRefreshDays.rejected, state => {
        state.saving = null;
      })
      .addCase(setUnusedUploadDays.pending, (state, action) => {
        state.saving = `${action.meta.arg.schema}:keep`;
      })
      .addCase(setUnusedUploadDays.fulfilled, (state, action) => {
        state.saving = null;
        state.overview = action.payload;
      })
      .addCase(setUnusedUploadDays.rejected, state => {
        state.saving = null;
      })
      .addCase(runRefreshCheck.pending, (state, action) => {
        const { schema } = action.meta.arg;
        if (!state.checking.includes(schema)) state.checking.push(schema);
      })
      .addCase(runRefreshCheck.fulfilled, (state, action) => {
        const { schema } = action.meta.arg;
        state.checking = state.checking.filter(s => s !== schema);
        state.refreshChecks[schema] = action.payload;
      })
      .addCase(runRefreshCheck.rejected, (state, action) => {
        const { schema } = action.meta.arg;
        state.checking = state.checking.filter(s => s !== schema);
        // F-UF-F16: the previous answer isn't this check's answer; the toast says
        // the check failed, so the row stops showing the old line.
        delete state.refreshChecks[schema];
      });
  },
});

export default accountPlanAccessSlice.reducer;
